import { useState } from 'react';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HistoricoPremiosModal from '../../../components/jogos/HistoricoPremiosModal';

interface Game {
  id: string;
  name: string;
  description: string;
  icon: string;
  cost: number;
  type: 'quiz' | 'wheel' | 'scratch' | 'memory';
  available: boolean;
}

interface GameHistory {
  id: string;
  game: string;
  date: string;
  cost: number;
  prize: string;
  status: 'won' | 'lost';
}

interface Prize {
  id: string;
  name: string;
  value: number;
  icon: string;
  color: string;
  image: string;
}

interface Winner {
  id: string;
  name: string;
  avatar: string;
  prize: string;
  prizeIcon: string;
  prizeColor: string;
  date: string;
  timeAgo: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  image: string;
}

interface BolaoMatch {
  id: string;
  fase: string;
  timeA: string;
  timeB: string;
  bandeirA: string;
  bandeirB: string;
  data: string;
  hora: string;
  placarA: number | null;
  placarB: number | null;
  encerrado: boolean;
}

interface BolaoAposta {
  matchId: string;
  placarA: number;
  placarB: number;
  xpGanho?: number;
  acertou?: boolean;
}

interface BolaoRanking {
  posicao: number;
  nome: string;
  avatar: string;
  pontos: number;
  acertos: number;
  xpGanho: number;
}

export default function JogosPage() {
  const { t } = useTranslation();
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showScratchCard, setShowScratchCard] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [showGeneralTutorial, setShowGeneralTutorial] = useState(false);

  // Bolão states
  const [showBolao, setShowBolao] = useState(false);
  const [bolaoApostas, setBolaoApostas] = useState<BolaoAposta[]>([]);
  const [bolaoTab, setBolaoTab] = useState<'apostar' | 'ranking' | 'resultados'>('apostar');
  const [bolaoConfirmado, setBolaoConfirmado] = useState(false);
  const [bolaoShowConfetti, setBolaoShowConfetti] = useState(false);

  // Quiz states
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<boolean[]>([]);

  // Wheel states
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonPrize, setWonPrize] = useState<Prize | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Scratch Card states
  const [isScratching, setIsScratching] = useState(false);
  const [scratchProgress, setScratchProgress] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showScratchResult, setShowScratchResult] = useState(false);
  const [scratchPrize, setScratchPrize] = useState<{ name: string; value: number; icon: string; color: string; image: string } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Scratch prizes definition - NOW USING REAL PRODUCTS
  const scratchPrizes = [
    { 
      name: 'Smartphone Galaxy', 
      value: 2000, 
      icon: 'ri-smartphone-line', 
      color: 'text-red-500', 
      image: 'https://readdy.ai/api/search-image?query=modern%20premium%20smartphone%20galaxy%20with%20sleek%20design%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-smartphone&orientation=squarish' 
    },
    { 
      name: 'Notebook Gamer', 
      value: 3000, 
      icon: 'ri-macbook-line', 
      color: 'text-teal-500', 
      image: 'https://readdy.ai/api/search-image?query=modern%20gaming%20laptop%20notebook%20computer%20open%20screen%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-notebook&orientation=squarish' 
    },
    { 
      name: 'Fone Bluetooth', 
      value: 500, 
      icon: 'ri-headphone-line', 
      color: 'text-green-500', 
      image: 'https://readdy.ai/api/search-image?query=wireless%20bluetooth%20headphones%20modern%20premium%20design%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-headphone&orientation=squarish' 
    },
    { 
      name: 'Smartwatch', 
      value: 1200, 
      icon: 'ri-time-line', 
      color: 'text-pink-500', 
      image: 'https://readdy.ai/api/search-image?query=modern%20smartwatch%20with%20digital%20display%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-smartwatch&orientation=squarish' 
    },
    { 
      name: 'Tablet Pro', 
      value: 1500, 
      icon: 'ri-tablet-line', 
      color: 'text-purple-500', 
      image: 'https://readdy.ai/api/search-image?query=modern%20tablet%20device%20with%20screen%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-tablet&orientation=squarish' 
    },
    { 
      name: 'Câmera Digital', 
      value: 2500, 
      icon: 'ri-camera-line', 
      color: 'text-rose-500', 
      image: 'https://readdy.ai/api/search-image?query=professional%20digital%20camera%20with%20lens%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=400&height=400&seq=scratch-prize-camera&orientation=squarish' 
    },
  ];

  // Quiz questions data
  const quizQuestions: QuizQuestion[] = [
    {
      id: '1',
      question: 'Qual é o principal benefício de um programa de incentivo para vendedores?',
      options: ['Aumentar custos operacionais', 'Motivar e engajar a equipe de vendas', 'Reduzir o número de funcionários', 'Diminuir metas de vendas'],
      correctAnswer: 1,
      image: 'https://readdy.ai/api/search-image?query=motivated%20sales%20team%20celebrating%20success%20in%20modern%20office%20environment%20professional%20business%20photography&width=600&height=300&seq=quiz-q1&orientation=landscape'
    },
    {
      id: '2',
      question: 'O que significa XP no contexto de gamificação?',
      options: ['Extra Prêmio', 'Experiência de Pontos', 'Pontos de Experiência', 'Extensão de Produto'],
      correctAnswer: 2,
      image: 'https://readdy.ai/api/search-image?query=gamification%20points%20experience%20level%20up%20digital%20interface%20colorful%20modern%20design&width=600&height=300&seq=quiz-q2&orientation=landscape'
    },
    {
      id: '3',
      question: 'Qual estratégia é mais eficaz para aumentar vendas?',
      options: ['Ignorar o cliente', 'Conhecer bem o produto e as necessidades do cliente', 'Vender apenas por preço', 'Evitar follow-up'],
      correctAnswer: 1,
      image: 'https://readdy.ai/api/search-image?query=sales%20strategy%20business%20meeting%20professional%20team%20discussing%20growth%20charts%20modern%20office&width=600&height=300&seq=quiz-q3&orientation=landscape'
    },
    {
      id: '4',
      question: 'O que é uma meta SMART?',
      options: ['Meta inteligente e tecnológica', 'Meta Específica, Mensurável, Atingível, Relevante e Temporal', 'Meta simples e rápida', 'Meta de tecnologia avançada'],
      correctAnswer: 1,
      image: 'https://readdy.ai/api/search-image?query=smart%20goals%20target%20achievement%20business%20planning%20whiteboard%20strategy%20modern%20office&width=600&height=300&seq=quiz-q4&orientation=landscape'
    },
    {
      id: '5',
      question: 'Qual é a importância do feedback no processo de vendas?',
      options: ['Não tem importância', 'Serve apenas para críticas', 'Ajuda a melhorar o desempenho e identificar oportunidades', 'Só é útil para gestores'],
      correctAnswer: 2,
      image: 'https://readdy.ai/api/search-image?query=feedback%20business%20performance%20review%20team%20discussion%20positive%20growth%20modern%20office%20professional&width=600&height=300&seq=quiz-q5&orientation=landscape'
    }
  ];

  // Bolão matches data
  const bolaoMatches: BolaoMatch[] = [
    {
      id: 'm1',
      fase: 'Fase de Grupos',
      timeA: 'Brasil',
      timeB: 'Argentina',
      bandeirA: '🇧🇷',
      bandeirB: '🇦🇷',
      data: '15/06/2026',
      hora: '16:00',
      placarA: null,
      placarB: null,
      encerrado: false
    },
    {
      id: 'm2',
      fase: 'Fase de Grupos',
      timeA: 'Brasil',
      timeB: 'França',
      bandeirA: '🇧🇷',
      bandeirB: '🇫🇷',
      data: '19/06/2026',
      hora: '13:00',
      placarA: null,
      placarB: null,
      encerrado: false
    },
    {
      id: 'm3',
      fase: 'Oitavas de Final',
      timeA: 'Brasil',
      timeB: 'Alemanha',
      bandeirA: '🇧🇷',
      bandeirB: '🇩🇪',
      data: '28/06/2026',
      hora: '20:00',
      placarA: null,
      placarB: null,
      encerrado: false
    },
    {
      id: 'm4',
      fase: 'Quartas de Final',
      timeA: 'Brasil',
      timeB: 'Portugal',
      bandeirA: '🇧🇷',
      bandeirB: '🇵🇹',
      data: '05/07/2026',
      hora: '16:00',
      placarA: null,
      placarB: null,
      encerrado: false
    },
    {
      id: 'm5',
      fase: 'Semifinal',
      timeA: 'Brasil',
      timeB: 'Espanha',
      bandeirA: '🇧🇷',
      bandeirB: '🇪🇸',
      data: '10/07/2026',
      hora: '20:00',
      placarA: null,
      placarB: null,
      encerrado: false
    },
    {
      id: 'm6',
      fase: 'Final',
      timeA: 'Brasil',
      timeB: 'Inglaterra',
      bandeirA: '🇧🇷',
      bandeirB: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      data: '15/07/2026',
      hora: '16:00',
      placarA: null,
      placarB: null,
      encerrado: false
    }
  ];

  // Bolão ranking data
  const bolaoRanking: BolaoRanking[] = [
    { posicao: 1, nome: 'Carlos Silva', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20portrait%20smiling%20confident%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-1&orientation=squarish', pontos: 340, acertos: 5, xpGanho: 680 },
    { posicao: 2, nome: 'Ana Costa', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20smiling%20confident%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-2&orientation=squarish', pontos: 280, acertos: 4, xpGanho: 560 },
    { posicao: 3, nome: 'Pedro Santos', avatar: 'https://readdy.ai/api/search-image?query=young%20professional%20man%20portrait%20smiling%20friendly%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-3&orientation=squarish', pontos: 240, acertos: 4, xpGanho: 480 },
    { posicao: 4, nome: 'Você', avatar: 'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20smiling%20cheerful%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-4&orientation=squarish', pontos: 200, acertos: 3, xpGanho: 400 },
    { posicao: 5, nome: 'Lucas Ferreira', avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20portrait%20smiling%20positive%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-5&orientation=squarish', pontos: 160, acertos: 3, xpGanho: 320 },
    { posicao: 6, nome: 'Juliana Alves', avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20smiling%20happy%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=bolao-rank-6&orientation=squarish', pontos: 120, acertos: 2, xpGanho: 240 },
  ];

  // Resultados encerrados do bolão
  const bolaoResultados = [
    { timeA: 'Brasil', timeB: 'México', bandeirA: '🇧🇷', bandeirB: '🇲🇽', placarA: 3, placarB: 1, fase: 'Amistoso', data: '10/03/2026' },
    { timeA: 'Brasil', timeB: 'Colômbia', bandeirA: '🇧🇷', bandeirB: '🇨🇴', placarA: 2, placarB: 0, fase: 'Amistoso', data: '25/03/2026' },
    { timeA: 'Brasil', timeB: 'Uruguai', bandeirA: '🇧🇷', bandeirB: '🇺🇾', placarA: 1, placarB: 1, fase: 'Eliminatórias', data: '05/04/2026' },
  ];

  const getApostaForMatch = (matchId: string) => {
    return bolaoApostas.find(a => a.matchId === matchId) || { matchId, placarA: 0, placarB: 0 };
  };

  const updateAposta = (matchId: string, field: 'placarA' | 'placarB', value: number) => {
    setBolaoApostas(prev => {
      const existing = prev.find(a => a.matchId === matchId);
      if (existing) {
        return prev.map(a => a.matchId === matchId ? { ...a, [field]: Math.max(0, value) } : a);
      }
      return [...prev, { matchId, placarA: field === 'placarA' ? Math.max(0, value) : 0, placarB: field === 'placarB' ? Math.max(0, value) : 0 }];
    });
  };

  const confirmarApostas = () => {
    setBolaoConfirmado(true);
    setBolaoShowConfetti(true);
    setTimeout(() => setBolaoShowConfetti(false), 4000);
  };

  // renderHistorico function
  const renderHistorico = () => {
    return (
      <HistoricoPremiosModal
        isOpen={showHistorico}
        onClose={() => setShowHistorico(false)}
      />
    );
  };

  // renderWheelGame function
  const renderWheelGame = () => {
    const segmentAngle = 360 / wheelSegments.length;

    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
            {Array.from({ length: 60 }).map((_, i) => (
              <div
                key={i}
                className="absolute w-3 h-3 rounded-sm animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF', '#FF8B94'][Math.floor(Math.random() * 5)],
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${0.5 + Math.random() * 1}s`
                }}
              />
            ))}
          </div>
        )}

        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => { setSelectedGame(null); setWonPrize(null); setShowConfetti(false); }}
              className="w-10 h-10 bg-white rounded-lg shadow flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-slate-700"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Roleta da Sorte</h1>
              <p className="text-slate-600 text-sm">Gire a roleta e ganhe prêmios incríveis!</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Roleta */}
            <div className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center">
              <div className="relative mb-6">
                {/* Ponteiro */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10 w-0 h-0" style={{ borderLeft: '12px solid transparent', borderRight: '12px solid transparent', borderTop: '24px solid #1e293b' }}></div>

                {/* Roleta SVG */}
                <div
                  className="w-72 h-72 rounded-full border-8 border-slate-800 shadow-2xl overflow-hidden"
                  style={{ transform: `rotate(${rotation}deg)`, transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none' }}
                >
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    {wheelSegments.map((segment, index) => {
                      const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                      const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                      const x1 = 100 + 100 * Math.cos(startAngle);
                      const y1 = 100 + 100 * Math.sin(startAngle);
                      const x2 = 100 + 100 * Math.cos(endAngle);
                      const y2 = 100 + 100 * Math.sin(endAngle);
                      const midAngle = ((index + 0.5) * segmentAngle - 90) * (Math.PI / 180);
                      const textX = 100 + 65 * Math.cos(midAngle);
                      const textY = 100 + 65 * Math.sin(midAngle);

                      return (
                        <g key={index}>
                          <path
                            d={`M 100 100 L ${x1} ${y1} A 100 100 0 0 1 ${x2} ${y2} Z`}
                            fill={segment.color}
                            stroke="white"
                            strokeWidth="1"
                          />
                          <text
                            x={textX}
                            y={textY}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill={segment.type === 'lose' ? '#64748b' : 'white'}
                            fontSize="10"
                            fontWeight="bold"
                            transform={`rotate(${(index + 0.5) * segmentAngle}, ${textX}, ${textY})`}
                          >
                            {segment.type === 'lose' ? '✕' : segment.number}
                          </text>
                        </g>
                      );
                    })}
                    <circle cx="100" cy="100" r="15" fill="#1e293b" />
                    <circle cx="100" cy="100" r="8" fill="white" />
                  </svg>
                </div>
              </div>

              <button
                onClick={spinWheel}
                disabled={isSpinning}
                className={`px-8 py-4 rounded-xl font-bold text-lg transition-all whitespace-nowrap ${
                  isSpinning
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-lg hover:scale-105 cursor-pointer'
                }`}
              >
                {isSpinning ? (
                  <><i className="ri-loader-4-line animate-spin mr-2"></i>Girando...</>
                ) : (
                  <><i className="ri-play-circle-line mr-2"></i>Girar Roleta (50 XP)</>
                )}
              </button>

              {wonPrize && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-green-300 rounded-xl text-center w-full">
                  <p className="text-green-700 font-bold text-lg">🎉 Parabéns! Você ganhou:</p>
                  <p className="text-green-900 font-black text-2xl mt-1">{wonPrize.name}</p>
                  <p className="text-green-600 text-sm mt-1">Valor: R$ {wonPrize.value.toLocaleString()}</p>
                </div>
              )}

              {wonPrize === null && !isSpinning && rotation > 0 && (
                <div className="mt-6 p-4 bg-slate-50 border-2 border-slate-200 rounded-xl text-center w-full">
                  <p className="text-slate-600 font-semibold">Não foi dessa vez... Tente novamente!</p>
                </div>
              )}
            </div>

            {/* Prêmios */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Prêmios Disponíveis</h3>
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {prizes.map((prize, index) => (
                  <div key={prize.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm" style={{ backgroundColor: prize.color }}>
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{prize.name}</p>
                      <p className="text-xs text-slate-500">R$ {prize.value.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Últimos Ganhadores */}
              <div className="mt-6">
                <h4 className="font-bold text-slate-800 mb-3">🏆 Últimos Ganhadores</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentWinners.slice(0, 5).map((winner) => (
                    <div key={winner.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <img src={winner.avatar} alt={winner.name} className="w-8 h-8 rounded-full object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{winner.name}</p>
                        <p className="text-xs text-slate-500">{winner.prize} · {winner.timeAgo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // renderQuizGame function
  const renderQuizGame = () => {
    const question = quizQuestions[currentQuestion];
    const totalQuestions = quizQuestions.length;

    if (quizFinished) {
      const xpGanho = quizScore * 20;
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-trophy-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-black text-slate-900 mb-2">Quiz Finalizado!</h2>
            <p className="text-slate-600 mb-6">Veja seu resultado abaixo</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-3xl font-black text-blue-700">{quizScore}/{totalQuestions}</p>
                <p className="text-sm text-blue-600">Acertos</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-3xl font-black text-green-700">+{xpGanho}</p>
                <p className="text-sm text-green-600">XP Ganhos</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowQuiz(false);
                  setSelectedGame(null);
                  setCurrentQuestion(0);
                  setQuizScore(0);
                  setQuizFinished(false);
                  setAnsweredQuestions([]);
                  setSelectedAnswer(null);
                  setShowResult(false);
                }}
                className="flex-1 px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Voltar aos Jogos
              </button>
              <button
                onClick={() => {
                  setCurrentQuestion(0);
                  setQuizScore(0);
                  setQuizFinished(false);
                  setAnsweredQuestions([]);
                  setSelectedAnswer(null);
                  setShowResult(false);
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Jogar Novamente
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => { setShowQuiz(false); setSelectedGame(null); setCurrentQuestion(0); setQuizScore(0); setQuizFinished(false); setAnsweredQuestions([]); setSelectedAnswer(null); setShowResult(false); }}
              className="w-10 h-10 bg-white rounded-lg shadow flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-slate-700"></i>
            </button>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Questão {currentQuestion + 1} de {totalQuestions}</span>
                <span className="text-sm font-bold text-blue-600">{quizScore} acertos</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${((currentQuestion) / totalQuestions) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <img src={question.image} alt="Quiz" className="w-full h-48 object-cover object-top" />
            <div className="p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-6">{question.question}</h2>
              <div className="space-y-3">
                {question.options.map((option, index) => {
                  let btnClass = 'w-full p-4 rounded-xl border-2 text-left font-medium transition-all cursor-pointer ';
                  if (showResult) {
                    if (index === question.correctAnswer) {
                      btnClass += 'border-green-400 bg-green-50 text-green-800';
                    } else if (index === selectedAnswer && index !== question.correctAnswer) {
                      btnClass += 'border-red-400 bg-red-50 text-red-800';
                    } else {
                      btnClass += 'border-slate-200 bg-slate-50 text-slate-500';
                    }
                  } else if (selectedAnswer === index) {
                    btnClass += 'border-blue-400 bg-blue-50 text-blue-800';
                  } else {
                    btnClass += 'border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-300 hover:bg-blue-50';
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => {
                        if (showResult) return;
                        setSelectedAnswer(index);
                      }}
                      className={btnClass}
                      disabled={showResult}
                    >
                      <span className="font-bold mr-3">{String.fromCharCode(65 + index)}.</span>
                      {option}
                    </button>
                  );
                })}
              </div>

              {showResult && (
                <div className={`mt-4 p-4 rounded-xl ${selectedAnswer === question.correctAnswer ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`font-semibold ${selectedAnswer === question.correctAnswer ? 'text-green-700' : 'text-red-700'}`}>
                    {selectedAnswer === question.correctAnswer ? '✅ Correto! +20 XP' : '❌ Incorreto!'}
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {!showResult ? (
                  <button
                    onClick={() => {
                      if (selectedAnswer === null) return;
                      setShowResult(true);
                      if (selectedAnswer === question.correctAnswer) {
                        setQuizScore(prev => prev + 1);
                      }
                    }}
                    disabled={selectedAnswer === null}
                    className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      selectedAnswer === null
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg cursor-pointer'
                    }`}
                  >
                    Confirmar Resposta
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (currentQuestion + 1 >= totalQuestions) {
                        setQuizFinished(true);
                      } else {
                        setCurrentQuestion(prev => prev + 1);
                        setSelectedAnswer(null);
                        setShowResult(false);
                      }
                    }}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    {currentQuestion + 1 >= totalQuestions ? 'Ver Resultado' : 'Próxima Questão'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // renderScratchCardGame function
  const renderScratchCardGame = () => {
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
      setIsDrawing(true);
      scratch(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      scratch(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    };

    const handleMouseUp = () => setIsDrawing(false);

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
      setIsDrawing(true);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const touch = e.touches[0];
      scratch(touch.clientX - rect.left, touch.clientY - rect.top);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing) return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const touch = e.touches[0];
      scratch(touch.clientX - rect.left, touch.clientY - rect.top);
    };

    const scratch = (x: number, y: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(x, y, 20, 0, Math.PI * 2);
      ctx.fill();

      // Calculate progress
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let transparent = 0;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] === 0) transparent++;
      }
      const progress = Math.round((transparent / (pixels.length / 4)) * 100);
      setScratchProgress(progress);

      if (progress >= 60 && !isRevealed) {
        setIsRevealed(true);
        setShowScratchResult(true);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => {
                setShowScratchCard(false);
                setSelectedGame(null);
                setScratchProgress(0);
                setIsRevealed(false);
                setShowScratchResult(false);
                setScratchPrize(null);
                setIsDrawing(false);
              }}
              className="w-10 h-10 bg-white rounded-lg shadow flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl text-slate-700"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Raspadinha</h1>
              <p className="text-slate-600 text-sm">Raspe e descubra seu prêmio!</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                <i className="ri-percent-line"></i>
                Progresso: {scratchProgress}%
              </div>
            </div>

            <div className="relative flex justify-center mb-6">
              {/* Prize behind */}
              {scratchPrize && (
                <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200" style={{ width: 400, height: 300, margin: '0 auto' }}>
                  <img src={scratchPrize.image} alt={scratchPrize.name} className="w-32 h-32 object-cover rounded-xl mb-3" />
                  <p className={`text-2xl font-black ${scratchPrize.color}`}>{scratchPrize.name}</p>
                  <p className="text-slate-600 font-semibold">R$ {scratchPrize.value.toLocaleString()}</p>
                </div>
              )}

              {/* Scratch canvas */}
              {!isRevealed && (
                <canvas
                  ref={canvasRef}
                  className="rounded-xl cursor-crosshair relative z-10"
                  style={{ touchAction: 'none' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleMouseUp}
                />
              )}
            </div>

            {showScratchResult && scratchPrize && (
              <div className="mt-4 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl text-center">
                <p className="text-2xl font-black text-orange-700 mb-2">🎉 Parabéns!</p>
                <p className="text-lg font-bold text-slate-800">Você ganhou: <span className={scratchPrize.color}>{scratchPrize.name}</span></p>
                <p className="text-slate-600 mt-1">Valor: R$ {scratchPrize.value.toLocaleString()}</p>
                <button
                  onClick={() => {
                    setShowScratchCard(false);
                    setSelectedGame(null);
                    setScratchProgress(0);
                    setIsRevealed(false);
                    setShowScratchResult(false);
                    setScratchPrize(null);
                    setIsDrawing(false);
                  }}
                  className="mt-4 px-8 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Resgatar Prêmio
                </button>
              </div>
            )}

            {!showScratchResult && (
              <p className="text-center text-slate-500 text-sm mt-4">
                Raspe pelo menos 60% da área para revelar o prêmio
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // renderBolaoGame function
  const renderBolaoGame = () => {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Header Copa */}
          <div className="bg-gradient-to-r from-green-600 via-green-500 to-yellow-500 p-6 text-white relative overflow-hidden">
            {bolaoShowConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {Array.from({ length: 30 }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-sm animate-bounce"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      backgroundColor: ['#FFD700', '#009C3B', '#FFFFFF'][Math.floor(Math.random() * 3)],
                      animationDelay: `${Math.random() * 1}s`,
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">⚽</div>
                <div>
                  <h2 className="text-2xl font-black">Bolão da Copa 🏆</h2>
                  <p className="text-green-100 text-sm">Copa do Mundo 2026 · Brasil 🇧🇷</p>
                </div>
              </div>
              <button
                onClick={() => { setShowBolao(false); setSelectedGame(null); setBolaoConfirmado(false); }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              {(['apostar', 'ranking', 'resultados'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setBolaoTab(tab)}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    bolaoTab === tab ? 'bg-white text-green-700' : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {tab === 'apostar' ? '⚽ Apostar' : tab === 'ranking' ? '🥇 Ranking' : '📊 Resultados'}
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
            {/* Aba Apostar */}
            {bolaoTab === 'apostar' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-800">Aposte nos Placares</h3>
                  <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                    <i className="ri-coin-line text-yellow-600"></i>
                    <span className="text-sm font-semibold text-yellow-700">Custo: 80 XP</span>
                  </div>
                </div>

                {bolaoConfirmado ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-black text-green-700 mb-2">Apostas Confirmadas! 🎉</h3>
                    <p className="text-slate-600 mb-6">Suas apostas foram registradas. Boa sorte!</p>
                    <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
                      <div className="bg-green-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-black text-green-700">{bolaoApostas.length}</p>
                        <p className="text-xs text-green-600">Apostas</p>
                      </div>
                      <div className="bg-yellow-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-black text-yellow-700">80</p>
                        <p className="text-xs text-yellow-600">XP Gastos</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-black text-blue-700">720</p>
                        <p className="text-xs text-blue-600">XP Possíveis</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {bolaoMatches.map((match) => {
                        const aposta = getApostaForMatch(match.id);
                        return (
                          <div key={match.id} className="border-2 border-slate-200 rounded-xl p-4 hover:border-green-300 transition-colors">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">{match.fase}</span>
                              <span className="text-xs text-slate-500">{match.data} · {match.hora}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex-1 text-center">
                                <p className="text-3xl mb-1">{match.bandeirA}</p>
                                <p className="font-bold text-slate-800">{match.timeA}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                  <button onClick={() => updateAposta(match.id, 'placarA', aposta.placarA - 1)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center font-bold cursor-pointer transition-colors">−</button>
                                  <div className="w-10 h-10 bg-green-50 border-2 border-green-300 rounded-lg flex items-center justify-center font-black text-xl text-green-700">{aposta.placarA}</div>
                                  <button onClick={() => updateAposta(match.id, 'placarA', aposta.placarA + 1)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center font-bold cursor-pointer transition-colors">+</button>
                                </div>
                                <span className="font-black text-slate-400 text-xl">×</span>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => updateAposta(match.id, 'placarB', aposta.placarB - 1)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center font-bold cursor-pointer transition-colors">−</button>
                                  <div className="w-10 h-10 bg-green-50 border-2 border-green-300 rounded-lg flex items-center justify-center font-black text-xl text-green-700">{aposta.placarB}</div>
                                  <button onClick={() => updateAposta(match.id, 'placarB', aposta.placarB + 1)} className="w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center font-bold cursor-pointer transition-colors">+</button>
                                </div>
                              </div>
                              <div className="flex-1 text-center">
                                <p className="text-3xl mb-1">{match.bandeirB}</p>
                                <p className="font-bold text-slate-800">{match.timeB}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-information-line text-green-600 text-lg mt-0.5"></i>
                        <div>
                          <p className="font-semibold text-green-800 text-sm">Sistema de Pontuação</p>
                          <p className="text-xs text-green-700 mt-1">Placar exato = 120 XP · Vencedor correto = 40 XP · Empate correto = 60 XP</p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={confirmarApostas}
                      className="w-full py-4 bg-gradient-to-r from-green-600 to-yellow-500 text-white font-black text-lg rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                    >
                      ⚽ Confirmar Apostas (80 XP)
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Aba Ranking */}
            {bolaoTab === 'ranking' && (
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4">🥇 Tabela de Classificação</h3>
                <div className="space-y-3">
                  {bolaoRanking.map((player) => (
                    <div
                      key={player.posicao}
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        player.nome === 'Você'
                          ? 'border-green-400 bg-green-50'
                          : player.posicao <= 3
                          ? 'border-yellow-200 bg-yellow-50'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-lg flex-shrink-0 ${
                        player.posicao === 1 ? 'bg-yellow-400 text-white' :
                        player.posicao === 2 ? 'bg-slate-300 text-white' :
                        player.posicao === 3 ? 'bg-orange-400 text-white' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {player.posicao === 1 ? '🥇' : player.posicao === 2 ? '🥈' : player.posicao === 3 ? '🥉' : player.posicao}
                      </div>
                      <img src={player.avatar} alt={player.nome} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold truncate ${player.nome === 'Você' ? 'text-green-800' : 'text-slate-800'}`}>{player.nome}</p>
                        <p className="text-xs text-slate-500">{player.acertos} acertos</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-black text-slate-800">{player.pontos} pts</p>
                        <p className="text-xs text-green-600 font-semibold">+{player.xpGanho} XP</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aba Resultados */}
            {bolaoTab === 'resultados' && (
              <div>
                <h3 className="text-lg font-bold text-slate-800 mb-4">📊 Resultados dos Jogos</h3>
                <div className="space-y-4">
                  {bolaoResultados.map((resultado, index) => (
                    <div key={index} className="border-2 border-slate-200 rounded-xl p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{resultado.fase}</span>
                        <span className="text-xs text-slate-500">{resultado.data}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 text-center">
                          <p className="text-3xl mb-1">{resultado.bandeirA}</p>
                          <p className="font-bold text-slate-800">{resultado.timeA}</p>
                        </div>
                        <div className="flex items-center gap-3 px-4">
                          <span className={`text-3xl font-black ${resultado.placarA > resultado.placarB ? 'text-green-600' : 'text-slate-400'}`}>{resultado.placarA}</span>
                          <span className="text-slate-400 font-bold">–</span>
                          <span className={`text-3xl font-black ${resultado.placarB > resultado.placarA ? 'text-green-600' : 'text-slate-400'}`}>{resultado.placarB}</span>
                        </div>
                        <div className="flex-1 text-center">
                          <p className="text-3xl mb-1">{resultado.bandeirB}</p>
                          <p className="font-bold text-slate-800">{resultado.timeB}</p>
                        </div>
                      </div>
                      <div className="mt-3 text-center">
                        {resultado.placarA > resultado.placarB ? (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">🇧🇷 Vitória do Brasil</span>
                        ) : resultado.placarA === resultado.placarB ? (
                          <span className="text-xs font-semibold text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">🤝 Empate</span>
                        ) : (
                          <span className="text-xs font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full">Derrota</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // useEffect for scratch card initialization - MOVED TO TOP LEVEL
  useEffect(() => {
    if (!showScratchCard) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    // Set canvas size with device pixel ratio for better quality on retina displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = 400 * dpr;
    canvas.height = 300 * dpr;
    
    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);
    
    // Set canvas display size
    canvas.style.width = '400px';
    canvas.style.height = '300px';

    // Draw scratch layer with full coverage
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(0, 0, 400, 300);

    // Add pattern
    ctx.fillStyle = '#cbd5e1';
    for (let i = 0; i < 400; i += 20) {
      for (let j = 0; j < 300; j += 20) {
        if ((i + j) % 40 === 0) {
          ctx.fillRect(i, j, 10, 10);
        }
      }
    }

    // Add text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('RASPE AQUI', 200, 140);
    ctx.font = '16px Inter, sans-serif';
    ctx.fillText('Use o mouse ou dedo', 200, 170);

    // Select random prize
    if (!scratchPrize) {
      const randomPrize = scratchPrizes[Math.floor(Math.random() * scratchPrizes.length)];
      setScratchPrize(randomPrize);
    }
  }, [showScratchCard, scratchPrize]);

  const stats = {
    xpSpent: 1250,
    prizesWon: 18,
    gamesPlayed: 45,
    winRate: 40
  };

  const games: Game[] = [
    {
      id: 1,
      name: 'Roleta da Sorte',
      description: 'Gire a roleta e ganhe prêmios',
      icon: 'ri-disc-line',
      cost: 50,
      type: 'wheel',
      available: true,
    },
    {
      id: 2,
      name: 'Quiz de Produtos',
      description: 'Teste seus conhecimentos',
      icon: 'ri-question-line',
      cost: 100,
      type: 'quiz',
      available: true,
    },
    {
      id: 3,
      name: 'Raspadinha',
      description: 'Raspe e descubra seu prêmio',
      icon: 'ri-gift-line',
      cost: 40,
      type: 'scratch',
      available: true,
    },
    {
      id: 4,
      name: 'Bolão da Copa 🏆',
      description: 'Aposte nos placares e ganhe XP',
      icon: 'ri-football-line',
      cost: 80,
      type: 'memory',
      available: true,
    },
  ];

  const history: GameHistory[] = [
    {
      id: '1',
      game: 'Roleta de Prêmios',
      date: '2024-01-15 14:30',
      cost: 50,
      prize: 'Smartphone Galaxy',
      status: 'won'
    },
    {
      id: '2',
      game: 'Quiz de Produtos',
      date: '2024-01-15 10:15',
      cost: 30,
      prize: '100 XP',
      status: 'won'
    },
    {
      id: '3',
      game: 'Roleta de Prêmios',
      date: '2024-01-14 16:45',
      cost: 50,
      prize: '-',
      status: 'lost'
    }
  ];

  const prizes: Prize[] = [
    {
      id: '1',
      name: 'Smartphone',
      value: 2000,
      icon: 'ri-smartphone-line',
      color: '#FF6B6B',
      image: 'https://readdy.ai/api/search-image?query=modern%20premium%20smartphone%20with%20sleek%20design%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-smartphone-1&orientation=squarish'
    },
    {
      id: '2',
      name: 'Notebook',
      value: 3000,
      icon: 'ri-macbook-line',
      color: '#4ECDC4',
      image: 'https://readdy.ai/api/search-image?query=modern%20laptop%20notebook%20computer%20open%20screen%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-notebook-2&orientation=squarish'
    },
    {
      id: '3',
      name: 'Fone Bluetooth',
      value: 500,
      icon: 'ri-headphone-line',
      color: '#95E1D3',
      image: 'https://readdy.ai/api/search-image?query=wireless%20bluetooth%20headphones%20modern%20design%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-headphone-3&orientation=squarish'
    },
    {
      id: '4',
      name: 'Smartwatch',
      value: 1200,
      icon: 'ri-time-line',
      color: '#F38181',
      image: 'https://readdy.ai/api/search-image?query=modern%20smartwatch%20with%20health%20monitoring%20display%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-smartwatch-4&orientation=squarish'
    },
    {
      id: '5',
      name: 'Tablet',
      value: 1500,
      icon: 'ri-tablet-line',
      color: '#AA96DA',
      image: 'https://readdy.ai/api/search-image?query=modern%20tablet%20device%20with%20screen%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-tablet-5&orientation=squarish'
    },
    {
      id: '6',
      name: 'Câmera',
      value: 2500,
      icon: 'ri-camera-line',
      color: '#FCBAD3',
      image: 'https://readdy.ai/api/search-image?query=professional%20digital%20camera%20with%20lens%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-camera-6&orientation=squarish'
    },
    {
      id: '7',
      name: 'Console',
      value: 2200,
      icon: 'ri-gamepad-line',
      color: '#FFFFD2',
      image: 'https://readdy.ai/api/search-image?query=modern%20gaming%20console%20with%20controller%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-console-7&orientation=squarish'
    },
    {
      id: '8',
      name: 'TV 50"',
      value: 3500,
      icon: 'ri-tv-line',
      color: '#A8E6CF',
      image: 'https://readdy.ai/api/search-image?query=modern%20smart%20television%20flat%20screen%20TV%20on%20clean%20white%20background%20professional%20product%20photography%20studio%20lighting%20high%20quality%20detailed%20realistic&width=300&height=300&seq=prize-tv-8&orientation=squarish'
    }
  ];

  const recentWinners: Winner[] = [
    {
      id: '1',
      name: 'Carlos Silva',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20smiling%20confident%20happy%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-1&orientation=squarish',
      prize: 'Smartphone',
      prizeIcon: 'ri-smartphone-line',
      prizeColor: '#FF6B6B',
      date: '2024-01-15 16:45',
      timeAgo: 'há 2 horas'
    },
    {
      id: '2',
      name: 'Ana Costa',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20smiling%20confident%20happy%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-2&orientation=squarish',
      prize: 'Notebook',
      prizeIcon: 'ri-macbook-line',
      prizeColor: '#4ECDC4',
      date: '2024-01-15 15:20',
      timeAgo: 'há 3 horas'
    },
    {
      id: '3',
      name: 'Pedro Santos',
      avatar: 'https://readdy.ai/api/search-image?query=young%20professional%20man%20portrait%20smiling%20friendly%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-3&orientation=squarish',
      prize: 'Smartwatch',
      prizeIcon: 'ri-time-line',
      prizeColor: '#F38181',
      date: '2024-01-15 14:10',
      timeAgo: 'há 4 horas'
    },
    {
      id: '4',
      name: 'Mariana Oliveira',
      avatar: 'https://readdy.ai/api/search-image?query=young%20professional%20woman%20portrait%20smiling%20cheerful%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-4&orientation=squarish',
      prize: 'Tablet',
      prizeIcon: 'ri-tablet-line',
      prizeColor: '#AA96DA',
      date: '2024-01-15 13:30',
      timeAgo: 'há 5 horas'
    },
    {
      id: '5',
      name: 'Lucas Ferreira',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20portrait%20smiling%20positive%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-5&orientation=squarish',
      prize: 'Fone Bluetooth',
      prizeIcon: 'ri-headphone-line',
      prizeColor: '#95E1D3',
      date: '2024-01-15 12:15',
      timeAgo: 'há 6 horas'
    },
    {
      id: '6',
      name: 'Juliana Alves',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20smiling%20happy%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-6&orientation=squarish',
      prize: 'Câmera',
      prizeIcon: 'ri-camera-line',
      prizeColor: '#FCBAD3',
      date: '2024-01-15 11:00',
      timeAgo: 'há 7 horas'
    },
    {
      id: '7',
      name: 'Rafael Souza',
      avatar: 'https://readdy.ai/api/search-image?query=young%20man%20portrait%20smiling%20confident%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-7&orientation=squarish',
      prize: 'Console',
      prizeIcon: 'ri-gamepad-line',
      prizeColor: '#FFFFD2',
      date: '2024-01-15 10:30',
      timeAgo: 'há 8 horas'
    },
    {
      id: '8',
      name: 'Camila Rocha',
      avatar: 'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20smiling%20friendly%20face%20headshot%20clean%20background%20realistic%20photography&width=100&height=100&seq=winner-avatar-8&orientation=squarish',
      prize: 'TV 50"',
      prizeIcon: 'ri-tv-line',
      prizeColor: '#A8E6CF',
      date: '2024-01-15 09:45',
      timeAgo: 'há 9 horas'
    }
  ];

  // Wheel segments with "Não foi dessa vez" options
  const wheelSegments = [
    { type: 'prize', prizeIndex: 0, number: 1, color: '#FF6B6B' },
    { type: 'lose', number: null, color: '#94A3B8' },
    { type: 'prize', prizeIndex: 1, number: 2, color: '#4ECDC4' },
    { type: 'lose', number: null, color: '#94A3B8' },
    { type: 'prize', prizeIndex: 2, number: 3, color: '#95E1D3' },
    { type: 'prize', prizeIndex: 3, number: 4, color: '#F38181' },
    { type: 'lose', number: null, color: '#94A3B8' },
    { type: 'prize', prizeIndex: 4, number: 5, color: '#AA96DA' },
    { type: 'prize', prizeIndex: 5, number: 6, color: '#FCBAD3' },
    { type: 'lose', number: null, color: '#94A3B8' },
    { type: 'prize', prizeIndex: 6, number: 7, color: '#FFFFD2' },
    { type: 'prize', prizeIndex: 7, number: 8, color: '#A8E6CF' }
  ];

  const spinWheel = () => {
    if (isSpinning) return;

    setIsSpinning(true);
    setWonPrize(null);
    setShowConfetti(false);

    // Random segment
    const randomSegmentIndex = Math.floor(Math.random() * wheelSegments.length);
    const selectedSegment = wheelSegments[randomSegmentIndex];
    
    // Calculate rotation (5 full spins + position)
    const segmentAngle = 360 / wheelSegments.length;
    const targetRotation = 360 * 5 + (360 - (randomSegmentIndex * segmentAngle + segmentAngle / 2));
    
    setRotation(targetRotation);

    setTimeout(() => {
      setIsSpinning(false);
      if (selectedSegment.type === 'prize') {
        setWonPrize(prizes[selectedSegment.prizeIndex]);
        setShowConfetti(true);
        // Hide confetti after 5 seconds
        setTimeout(() => setShowConfetti(false), 5000);
      } else {
        setWonPrize(null);
      }
    }, 4000);
  };

  const handleGameClick = (game: Game) => {
    if (!game.available) return;

    if (game.type === 'memory') {
      setSelectedGame(game);
      setShowBolao(true);
      return;
    }

    setSelectedGame(game);
    setShowTutorial(true);
  };

  const iniciarJogo = () => {
    setShowTutorial(false);
    
    if (selectedGame?.type === 'quiz') {
      setShowQuiz(true);
    } else if (selectedGame?.type === 'scratch') {
      setShowScratchCard(true);
    } else if (selectedGame?.type === 'wheel') {
      // Não precisa fazer nada, apenas fecha o tutorial e volta para a roleta
    }
  };

  // Tutoriais por tipo de jogo
  const tutoriais = {
    wheel: {
      titulo: 'Como Jogar: Roleta da Sorte',
      icone: 'ri-disc-line',
      cor: 'from-orange-500 to-red-500',
      passos: [
        {
          icone: 'ri-coin-line',
          titulo: 'Custo do Jogo',
          descricao: 'Cada rodada custa 50 XP. Certifique-se de ter XP suficiente antes de jogar.'
        },
        {
          icone: 'ri-play-circle-line',
          titulo: 'Gire a Roleta',
          descricao: 'Clique no botão "Girar Roleta" e aguarde a roleta parar em um dos prêmios.'
        },
        {
          icone: 'ri-trophy-line',
          titulo: 'Ganhe Prêmios',
          descricao: 'Se a roleta parar em um número, você ganha o prêmio correspondente! Boa sorte!'
        },
        {
          icone: 'ri-refresh-line',
          titulo: 'Jogue Novamente',
          descricao: 'Você pode jogar quantas vezes quiser, sempre que tenha XP disponível.'
        }
      ]
    },
    quiz: {
      titulo: 'Como Jogar: Quiz de Produtos',
      icone: 'ri-question-line',
      cor: 'from-blue-500 to-purple-500',
      passos: [
        {
          icone: 'ri-coin-line',
          titulo: 'Custo do Jogo',
          descricao: 'Cada quiz custa 100 XP e contém 5 questões sobre produtos.'
        },
        {
          icone: 'ri-checkbox-circle-line',
          titulo: 'Responda as Questões',
          descricao: 'Leia cada questão com atenção e selecione a resposta correta entre as 4 opções.'
        },
        {
          icone: 'ri-star-line',
          titulo: 'Ganhe Pontos',
          descricao: 'Cada resposta correta vale 20 XP. Acerte pelo menos 3 questões para ganhar!'
        },
        {
          icone: 'ri-trophy-line',
          titulo: 'Resultado Final',
          descricao: 'Ao final, veja sua pontuação e os XP ganhos. Tente fazer 100% de acertos!'
        }
      ]
    },
    scratch: {
      titulo: 'Como Jogar: Raspadinha',
      icone: 'ri-gift-line',
      cor: 'from-green-500 to-teal-500',
      passos: [
        {
          icone: 'ri-coin-line',
          titulo: 'Custo do Jogo',
          descricao: 'Cada raspadinha custa 40 XP. É o jogo mais acessível!'
        },
        {
          icone: 'ri-hand-coin-line',
          titulo: 'Raspe a Área',
          descricao: 'Use o mouse ou dedo para raspar a área cinza e revelar o prêmio escondido.'
        },
        {
          icone: 'ri-percent-line',
          titulo: 'Progresso',
          descricao: 'Você precisa raspar pelo menos 60% da área para revelar completamente o prêmio.'
        },
        {
          icone: 'ri-gift-2-line',
          titulo: 'Descubra o Prêmio',
          descricao: 'Cada raspadinha contém um prêmio surpresa! Pode ser produtos incríveis!'
        }
      ]
    },
    memory: {
      titulo: 'Como Jogar: Caça ao Tesouro',
      icone: 'ri-treasure-map-line',
      cor: 'from-purple-500 to-pink-500',
      passos: [
        {
          icone: 'ri-coin-line',
          titulo: 'Custo do Jogo',
          descricao: 'Cada partida custa 150 XP. É um desafio de memória!'
        },
        {
          icone: 'ri-eye-line',
          titulo: 'Memorize os Pares',
          descricao: 'Observe as cartas viradas e memorize suas posições.'
        },
        {
          icone: 'ri-focus-3-line',
          titulo: 'Encontre os Pares',
          descricao: 'Clique em duas cartas por vez para encontrar os pares correspondentes.'
        },
        {
          icone: 'ri-timer-line',
          titulo: 'Complete Rápido',
          descricao: 'Quanto mais rápido completar, mais XP você ganha como bônus!'
        }
      ]
    }
  };

  // Modal de Tutorial
  const renderTutorial = () => {
    if (!selectedGame || !showTutorial) return null;

    const tutorial = tutoriais[selectedGame.type];

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className={`bg-gradient-to-r ${tutorial.cor} p-6 rounded-t-2xl`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className={`${tutorial.icone} text-2xl text-white`}></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{tutorial.titulo}</h2>
                  <p className="text-white/90 text-sm">Aprenda a jogar em 4 passos simples</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTutorial(false);
                  setSelectedGame(null);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Fechar tutorial"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="p-8">
            <div className="space-y-6">
              {tutorial.passos.map((passo, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tutorial.cor} flex items-center justify-center flex-shrink-0 text-white font-bold text-lg`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <i className={`${passo.icone} text-2xl text-slate-600`}></i>
                      <h3 className="text-lg font-bold text-slate-900">{passo.titulo}</h3>
                    </div>
                    <p className="text-slate-600">{passo.descricao}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Dica Extra */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-xl text-blue-600 mt-0.5"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">💡 Dica Importante</h4>
                  <p className="text-sm text-blue-800">
                    Todos os jogos custam XP, mas você pode ganhar muito mais de volta! 
                    Jogue com estratégia e aproveite os bônus especiais.
                  </p>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => {
                  setShowTutorial(false);
                  setSelectedGame(null);
                }}
                className="flex-1 px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-colors whitespace-nowrap"
              >
                Voltar
              </button>
              <button
                onClick={iniciarJogo}
                className={`flex-1 px-6 py-3 bg-gradient-to-r ${tutorial.cor} text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap`}
              >
                <i className="ri-play-line mr-2"></i>
                Começar a Jogar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal de Tutorial Geral (todos os jogos)
  const renderGeneralTutorial = () => {
    if (!showGeneralTutorial) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-gamepad-line text-2xl text-white"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Como Jogar</h2>
                  <p className="text-white/90 text-sm">Aprenda a jogar todos os jogos disponíveis</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowGeneralTutorial(false);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Fechar tutorial"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="p-8">
            <div className="space-y-8">
              {/* Roleta da Sorte */}
              <div className="border-2 border-orange-200 rounded-xl p-6 bg-gradient-to-br from-orange-50 to-orange-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <i className="ri-disc-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Roleta da Sorte</h3>
                    <p className="text-sm text-slate-600">Custo: 50 XP por rodada</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                    <div>
                      <p className="font-semibold text-slate-800">Clique em "Girar Roleta"</p>
                      <p className="text-sm text-slate-600">Cada rodada custa 50 XP</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                    <div>
                      <p className="font-semibold text-slate-800">Aguarde a roleta parar</p>
                      <p className="text-sm text-slate-600">A roleta girará e parará em um número ou "Não foi dessa vez"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                    <div>
                      <p className="font-semibold text-slate-800">Ganhe prêmios incríveis</p>
                      <p className="text-sm text-slate-600">Se parar em um número, você ganha o prêmio correspondente!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quiz de Produtos */}
              <div className="border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <i className="ri-question-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Quiz de Produtos</h3>
                    <p className="text-sm text-slate-600">Custo: 100 XP por quiz</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                    <div>
                      <p className="font-semibold text-slate-800">Responda 5 questões</p>
                      <p className="text-sm text-slate-600">Cada quiz contém 5 perguntas sobre produtos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                    <div>
                      <p className="font-semibold text-slate-800">Ganhe 20 XP por acerto</p>
                      <p className="text-sm text-slate-600">Cada resposta correta vale 20 XP</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                    <div>
                      <p className="font-semibold text-slate-800">Acerte pelo menos 3 questões</p>
                      <p className="text-sm text-slate-600">Você precisa de 3 acertos para ganhar XP</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Raspadinha */}
              <div className="border-2 border-green-200 rounded-xl p-6 bg-gradient-to-br from-green-50 to-teal-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <i className="ri-gift-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Raspadinha</h3>
                    <p className="text-sm text-slate-600">Custo: 40 XP por raspadinha</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                    <div>
                      <p className="font-semibold text-slate-800">Raspe a área cinza</p>
                      <p className="text-sm text-slate-600">Use o mouse ou dedo para raspar</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                    <div>
                      <p className="font-semibold text-slate-800">Raspe pelo menos 60%</p>
                      <p className="text-sm text-slate-600">Você precisa raspar 60% da área para revelar o prêmio</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                    <div>
                      <p className="font-semibold text-slate-800">Descubra seu prêmio</p>
                      <p className="text-sm text-slate-600">Cada raspadinha contém um prêmio surpresa!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Caça ao Tesouro */}
              <div className="border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-pink-50 opacity-60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <i className="ri-treasure-map-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Caça ao Tesouro</h3>
                    <p className="text-sm text-slate-600">Custo: 150 XP por partida</p>
                  </div>
                  <span className="ml-auto text-xs font-medium text-slate-500 bg-slate-200 px-3 py-1 rounded-full whitespace-nowrap">
                    Em breve
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 text-white font-bold">1</div>
                    <div>
                      <p className="font-semibold text-slate-800">Memorize os pares</p>
                      <p className="text-sm text-slate-600">Observe as cartas e memorize suas posições</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 text-white font-bold">2</div>
                    <div>
                      <p className="font-semibold text-slate-800">Encontre os pares</p>
                      <p className="text-sm text-slate-600">Clique em duas cartas por vez para encontrar os pares</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 text-white font-bold">3</div>
                    <div>
                      <p className="font-semibold text-slate-800">Complete rápido</p>
                      <p className="text-sm text-slate-600">Quanto mais rápido, mais XP você ganha!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dica Extra */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-xl text-blue-600 mt-0.5"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">💡 Dica Importante</h4>
                  <p className="text-sm text-blue-800">
                    Todos os jogos custam XP, mas você pode ganhar muito mais de volta! 
                    Jogue com estratégia e aproveite os bônus especiais. Confira seu histórico de prêmios para ver tudo que já ganhou!
                  </p>
                </div>
              </div>
            </div>

            {/* Botão Fechar */}
            <div className="mt-8">
              <button
                onClick={() => {
                  setShowGeneralTutorial(false);
                }}
                className="w-full px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap"
              >
                Entendi, vamos jogar!
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (selectedGame?.type === 'wheel') {
    return renderWheelGame();
  }

  if (selectedGame?.type === 'quiz' && showQuiz) {
    return renderQuizGame();
  }

  if (showScratchCard) {
    return renderScratchCardGame();
  }

  return (
    <>
      {/* Renderizar Tutorial */}
      {renderTutorial()}

      {/* Renderizar Tutorial Geral */}
      {renderGeneralTutorial()}

      {/* Renderizar Histórico */}
      {renderHistorico()}

      {/* Renderizar Bolão */}
      {showBolao && renderBolaoGame()}

      {/* Botões no topo */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowGeneralTutorial(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-question-line text-xl"></i>
          Como Jogar
        </button>

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowHistorico(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-trophy-line text-xl"></i>
          Ver Histórico de Prêmios
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">XP Gasto</p>
              <p className="text-2xl font-bold text-orange-700 mt-1">{stats.xpSpent}</p>
            </div>
            <div className="w-12 h-12 bg-orange-200 rounded-lg flex items-center justify-center">
              <i className="ri-coin-line text-2xl text-orange-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Prêmios Ganhos</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{stats.prizesWon}</p>
            </div>
            <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
              <i className="ri-gift-line text-2xl text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Jogos Realizados</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">{stats.gamesPlayed}</p>
            </div>
            <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
              <i className="ri-gamepad-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Taxa de Vitória</p>
              <p className="text-2xl font-bold text-purple-700 mt-1">{stats.winRate}%</p>
            </div>
            <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
              <i className="ri-trophy-line text-2xl text-purple-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Jogos Disponíveis</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {games.map((game) => (
            <button
              key={game.id}
              onClick={() => handleGameClick(game)}
              disabled={!game.available}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                game.type === 'memory' && game.available
                  ? 'border-yellow-300 bg-gradient-to-br from-green-50 via-yellow-50 to-green-50 hover:border-yellow-500 hover:shadow-lg cursor-pointer relative overflow-hidden'
                  : game.available
                  ? 'border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 hover:border-orange-400 hover:shadow-lg cursor-pointer'
                  : 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
              }`}
              aria-label={`Jogar ${game.name}`}
            >
              {game.type === 'memory' && game.available && (
                <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-400 to-green-500 text-white text-xs font-black px-3 py-1 rounded-bl-xl">
                  🇧🇷 NOVO
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  game.type === 'memory' && game.available
                    ? 'bg-gradient-to-br from-green-500 to-yellow-400'
                    : game.available ? 'bg-orange-200' : 'bg-slate-200'
                }`}>
                  <i className={`${game.icon} text-2xl ${
                    game.type === 'memory' && game.available
                      ? 'text-white'
                      : game.available ? 'text-orange-600' : 'text-slate-400'
                  }`}></i>
                </div>
                {!game.available && (
                  <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-1 rounded-full whitespace-nowrap">
                    Em breve
                  </span>
                )}
              </div>
              <h3 className={`font-bold mb-1 ${
                game.type === 'memory' && game.available
                  ? 'text-green-800'
                  : game.available ? 'text-slate-800' : 'text-slate-500'
              }`}>
                {game.name}
              </h3>
              <p className={`text-sm mb-3 ${
                game.type === 'memory' && game.available
                  ? 'text-green-700'
                  : game.available ? 'text-slate-600' : 'text-slate-400'
              }`}>
                {game.description}
              </p>
              <div className={`flex items-center gap-1 text-sm font-semibold ${
                game.type === 'memory' && game.available
                  ? 'text-green-700'
                  : game.available ? 'text-orange-600' : 'text-slate-400'
              }`}>
                <i className="ri-coin-line"></i>
                <span>{game.cost} XP</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Histórico de Jogadas</h3>
        <div className="space-y-3">
          {history.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  item.status === 'won' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <i className={`${
                    item.status === 'won' ? 'ri-trophy-line text-green-600' : 'ri-close-line text-red-600'
                  } text-xl`}></i>
                </div>
                <div>
                  <p className="font-medium text-slate-800">{item.game}</p>
                  <p className="text-sm text-slate-500">{item.date}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-slate-800">{item.prize}</p>
                <p className="text-sm text-slate-500">-{item.cost} XP</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}